import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';

export default function AdminLeads() {
  const { api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAdvisorId, setSelectedAdvisorId] = useState('');
  const [filter, setFilter] = useState('all'); // all, assigned, pending
  const [savingMeetingId, setSavingMeetingId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [leadsData, advisorsData] = await Promise.all([
        api('/admin/leads'),
        api('/admin/advisors')
      ]);
      setLeads(leadsData.leads);
      setAdvisors(advisorsData.advisors);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark (or unmark) a lead as having turned into a booked meeting. Admins can
  // do this for any lead, for when an advisor reports it by email rather than
  // pressing the button on their own dashboard.
  const toggleMeeting = async (lead) => {
    const booked = !lead.meeting_booked;
    setSavingMeetingId(lead.id);
    try {
      const data = await api(`/admin/leads/${lead.id}/meeting`, {
        method: 'POST',
        body: JSON.stringify({ booked })
      });
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, ...data.lead } : l)));
    } catch (error) {
      console.error('Failed to update meeting status:', error);
      alert('Sorry, that did not save. Please try again.');
    } finally {
      setSavingMeetingId(null);
    }
  };

  const assignLead = async () => {
    if (!selectedLead || !selectedAdvisorId) return;

    try {
      await api(`/admin/leads/${selectedLead.id}/assign`, {
        method: 'POST',
        body: JSON.stringify({ advisorId: parseInt(selectedAdvisorId) })
      });
      setShowAssignModal(false);
      setSelectedLead(null);
      setSelectedAdvisorId('');
      loadData();
    } catch (error) {
      console.error('Failed to assign lead:', error);
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filter === 'assigned') return lead.status === 'assigned';
    if (filter === 'pending') return lead.status === 'pending';
    return true;
  });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl text-navy-900 mb-2">All Leads</h1>
          <p className="text-navy-600">{leads.length} total leads submitted</p>
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-navy-900 text-white' 
                : 'bg-cream-100 text-navy-600 hover:bg-cream-200'
            }`}
          >
            All ({leads.length})
          </button>
          <button
            onClick={() => setFilter('assigned')}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              filter === 'assigned' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-cream-100 text-navy-600 hover:bg-cream-200'
            }`}
          >
            Assigned ({leads.filter(l => l.status === 'assigned').length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              filter === 'pending' 
                ? 'bg-amber-600 text-white' 
                : 'bg-cream-100 text-navy-600 hover:bg-cream-200'
            }`}
          >
            Pending ({leads.filter(l => l.status === 'pending').length})
          </button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-cream-50 border-b border-cream-200">
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Lead</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Contact</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Meeting</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Assigned To</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Status</th>
                <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Date</th>
                <th className="text-right text-xs font-medium text-navy-500 uppercase tracking-wider py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr key={lead.id} className="border-b border-cream-100 hover:bg-cream-50">
                  <td className="py-4 px-4">
                    <p className="font-medium text-navy-900 cursor-help" title={lead.message}>{lead.name}</p>
                    {lead.budget && (
                      <p className="text-xs text-navy-500 mt-0.5 whitespace-nowrap">
                        {lead.budget.replace(/\s*per person\s*$/i, '')}
                      </p>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <a href={`mailto:${lead.email}`} className="text-gold-600 hover:text-gold-700 text-sm block">
                      {lead.email}
                    </a>
                    {lead.phone && (
                      <a href={`tel:${lead.phone}`} className="text-navy-500 text-sm">
                        {lead.phone}
                      </a>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    {lead.meeting_booked ? (
                      <div>
                        <span className="badge badge-success whitespace-nowrap">Meeting booked</span>
                        <button
                          onClick={() => toggleMeeting(lead)}
                          disabled={savingMeetingId === lead.id}
                          className="block text-xs text-navy-500 underline mt-1 hover:text-navy-700 disabled:opacity-50"
                        >
                          {savingMeetingId === lead.id ? 'Saving...' : 'Undo'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => toggleMeeting(lead)}
                        disabled={savingMeetingId === lead.id}
                        title="An actual meeting happened or is in the diary. This is not a booked trip."
                        className="text-gold-600 hover:text-gold-700 text-sm font-medium text-left leading-tight disabled:opacity-50"
                      >
                        {savingMeetingId === lead.id ? 'Saving...' : 'Mark meeting booked'}
                      </button>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    {lead.first_name ? (
                      <div>
                        <p className="text-navy-900">{lead.first_name} {lead.last_name}</p>
                        <p className="text-xs text-navy-500">{lead.advisor_email}</p>
                      </div>
                    ) : (
                      <span className="text-navy-400 italic">Unassigned</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <span className={`badge ${lead.status === 'assigned' ? 'badge-success' : 'badge-warning'}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-navy-500 text-sm whitespace-nowrap">
                    {new Date(lead.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </td>
                  <td className="py-4 px-4 text-right">
                    {lead.status === 'pending' && (
                      <button
                        onClick={() => {
                          setSelectedLead(lead);
                          setShowAssignModal(true);
                        }}
                        className="text-gold-600 hover:text-gold-700 text-sm font-medium"
                      >
                        Assign
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLeads.length === 0 && (
          <div className="text-center py-12 text-navy-500">
            No leads found
          </div>
        )}
      </div>

      {/* Assign Lead Modal */}
      {showAssignModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-luxury-lg max-w-lg w-full p-6">
            <h3 className="font-serif text-xl text-navy-900 mb-2">Assign Lead</h3>
            <p className="text-navy-600 mb-4">
              Manually assign this lead to an advisor
            </p>

            {/* Lead Info */}
            <div className="bg-cream-50 rounded-lg p-4 mb-6">
              <p className="font-medium text-navy-900">{selectedLead.name}</p>
              <p className="text-sm text-navy-600">{selectedLead.email}</p>
              <p className="text-sm text-navy-500 mt-2">{selectedLead.message}</p>
            </div>

            {/* Advisor Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-navy-700 mb-2">
                Select Advisor
              </label>
              <select
                value={selectedAdvisorId}
                onChange={(e) => setSelectedAdvisorId(e.target.value)}
                className="input"
              >
                <option value="">Choose an advisor...</option>
                {advisors.map((advisor) => (
                  <option key={advisor.id} value={advisor.id}>
                    {advisor.firstName} {advisor.lastName} — {advisor.balanceLeads} leads remaining
                    {advisor.isPaused ? ' (Paused)' : ''}
                    {!advisor.isActive ? ' (Inactive)' : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-navy-500 mt-2">
                Note: Assigning to an advisor with balance will deduct 1 lead from their account
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedLead(null);
                  setSelectedAdvisorId('');
                }}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button 
                onClick={assignLead} 
                disabled={!selectedAdvisorId}
                className="btn-primary flex-1"
              >
                Assign Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
